/**
 * SiteSelector — pisike header-i dropdown valitud sait-i jaoks.
 *
 * Tõmbab GET /api/sites loendi ja hoiab valitud slot-i localStorage-is.
 * Kui kasutaja vahetab → emit window.dispatchEvent('site-changed').
 * Komponendid mis tahavad per-site andmeid, kuulavad selle eventi peal
 * ja teevad oma fetch /api/sites/:slot/* peale.
 */

import { useEffect, useState } from 'react';
import { ChevronDown, MapPin, RotateCw } from 'lucide-react';

const STORAGE_KEY = 'rohepaneel:selectedSlot';

export function getSelectedSlot() {
  // Tagasi-ühilduvus: kasuta vana lynxpower:* võtit kui uut pole veel salvestatud
  const v = localStorage.getItem(STORAGE_KEY) ?? localStorage.getItem('lynxpower:selectedSlot');
  return v ? parseInt(v) : null;
}

export function setSelectedSlot(slot) {
  if (slot == null) localStorage.removeItem(STORAGE_KEY);
  else localStorage.setItem(STORAGE_KEY, String(slot));
  window.dispatchEvent(new CustomEvent('site-changed', { detail: { slot } }));
}

export function SiteSelector() {
  const [sites, setSites] = useState([]);
  const [mainSlot, setMainSlot] = useState(null);
  const [selected, setSelected] = useState(getSelectedSlot());
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const fetchSites = () => {
      fetch('/api/sites', { cache: 'no-store' })
        .then(r => r.json())
        .then(d => {
          setSites(d.sites || []);
          setMainSlot(d.mainSlot);
          // Kui veel pole valitud, vali main
          if (selected == null && d.mainSlot != null) {
            setSelected(d.mainSlot);
            setSelectedSlot(d.mainSlot);
          }
        })
        .catch(() => {});
    };
    fetchSites();
    const id = setInterval(fetchSites, 30000);  // refresh iga 30s
    return () => clearInterval(id);
  }, []);

  const [reloading, setReloading] = useState(false);

  const handleSelect = (slot) => {
    setSelected(slot);
    setSelectedSlot(slot);
    setOpen(false);
  };

  const handleReload = async (e) => {
    e.stopPropagation();
    setReloading(true);
    try {
      const res = await fetch('/api/sites/reload', { method: 'POST' });
      const d = await res.json();
      if (d.sites) {
        setSites(d.sites);
        setMainSlot(d.mainSlot);
      }
    } finally {
      setTimeout(() => setReloading(false), 500);
    }
  };

  const current = sites.find(s => s.slot === selected);

  if (sites.length === 0) return null;
  if (sites.length === 1) {
    // Üks sait — kuva ainult nime, mitte dropdown
    return (
      <div className="flex items-center gap-1.5 text-[11px] text-zinc-700">
        <MapPin className="w-3 h-3" strokeWidth={1.75} />
        <span>{sites[0].name}</span>
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1.5 text-[11px] px-2 py-1 rounded border border-[var(--border)] bg-white hover:bg-zinc-50"
      >
        <MapPin className="w-3 h-3" strokeWidth={1.75} />
        <span className="font-medium text-zinc-800">
          {current?.name || `Slot ${selected}`}
        </span>
        <ChevronDown className="w-3 h-3" strokeWidth={1.75} />
      </button>
      {open && (
        <div className="absolute right-0 mt-1 min-w-[220px] bg-white border border-[var(--border)] rounded shadow-lg z-50">
          <div className="border-b border-zinc-100 px-3 py-1.5 flex items-center justify-between text-[10px] text-zinc-500 uppercase tracking-wider">
            <span>Saidid ({sites.length})</span>
            <button
              onClick={handleReload}
              className="flex items-center gap-1 hover:text-zinc-800"
              title="Loe parks.json uuesti (UI muudatuste järel)"
            >
              <RotateCw className={`w-3 h-3 ${reloading ? 'animate-spin' : ''}`} strokeWidth={1.75} />
              Reload
            </button>
          </div>
          {sites.map(s => {
            const isMain = s.slot === mainSlot;
            const isSel = s.slot === selected;
            const onlineCount = Object.values(s.connected || {}).filter(Boolean).length;
            return (
              <button
                key={s.slot}
                onClick={() => handleSelect(s.slot)}
                className={`w-full text-left px-3 py-1.5 text-[11px] hover:bg-zinc-50 flex items-center justify-between ${isSel ? 'bg-blue-50' : ''}`}
              >
                <span className="flex items-center gap-1.5">
                  <span className={`w-1.5 h-1.5 rounded-full ${onlineCount >= 3 ? 'bg-green-500' : onlineCount > 0 ? 'bg-amber-500' : 'bg-zinc-300'}`} />
                  <span className="text-zinc-800">{s.name}</span>
                  {isMain && <span className="text-[9px] text-zinc-400">main</span>}
                </span>
                <span className="text-[9px] text-zinc-400">slot {s.slot}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
